import { createFileRoute, Link, useRouter } from '@tanstack/react-router';
import GoogleIcon from 'src/assets/icons/google-logo.svg';
// import FacebookIcon from 'src/assets/icons/facebook-logo.svg';
import AppleIcon from 'src/assets/icons/apple-logo.svg';
import BackArrow from 'src/assets/icons/back-arrow.svg';
// import { isEmpty } from 'lodash';
import { getUserId } from 'src/services/user.service';
import * as Sentry from '@sentry/react';

import {
  // FacebookAuthProvider,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
} from 'firebase/auth';
import { auth } from 'src/configs/firebase';
import Button, { ButtonType } from 'src/components/ui/button';
import React, { useEffect } from 'react';
import RadioButton from 'src/components/ui/radio-button';
import useUserStore from 'src/stores/user';
import { useMutation } from '@tanstack/react-query';
import { loginSocial } from 'src/services/auth.service';
import { StorageKey } from 'src/constants';
import { isEmpty } from 'lodash';

export const Route = createFileRoute('/login')({
  component: RouteComponent,
});

function RouteComponent() {
  const { user, setUser } = useUserStore();
  const router = useRouter();
  const [isTermsAccepted, setIsTermsAccepted] = React.useState(false);
  const [isPrivacyAccepted, setIsPrivacyAccepted] = React.useState(false);
  const userId = getUserId();
  useEffect(() => {
    return () => {
      // Cleanup function to reset the state when the component unmounts
      setIsTermsAccepted(false);
      setIsPrivacyAccepted(false);
    };
  }, []);

  const isAuth = React.useMemo(() => {
    return !isEmpty(user);
  }, [user]);

  const mutateLogin = useMutation({
    mutationFn: loginSocial,
    onSuccess(data) {
      if (!data.data) {
        Sentry.captureException('Login failed: No data received');
        console.error('Login failed: No data received');
        return;
      }
      setUser(data.data.user);
      localStorage.setItem(StorageKey.ACCESS_TOKEN, data.data.accessToken);
      localStorage.setItem(StorageKey.REFRESH_TOKEN, data.data.refreshToken);
    },
    onError(error) {
      // toast.error(error.message);
      Sentry.captureException(error);
      console.error('Login error:', error);
    },
  });

  const handleGoogleSignIn = async () => {
    Sentry.addBreadcrumb({
      message: 'Login with Google',
      level: 'info',
      category: 'event',
    });
    const provider = new GoogleAuthProvider();
    try {
      const result = await signInWithPopup(auth, provider);

      // The signed-in user info.
      const user = result.user;

      const firebaseToken = await user.getIdToken();
      mutateLogin.mutate({ firebaseToken });
    } catch (error) {
      Sentry.captureException(error);
      console.log('Error: ', error);
    }
  };

  // const handleFacebookSignIn = async () => {
  //   const provider = new FacebookAuthProvider();
  //   try {
  //     const result = await signInWithPopup(auth, provider);

  //     // The signed-in user info.
  //     const user = result.user;

  //     const firebaseToken = await user.getIdToken();
  //     mutateLogin.mutate({ firebaseToken });
  //   } catch (error) {
  //     console.log('Error: ', error);
  //   }
  // };

  const handleAppleSignIn = async () => {
    Sentry.addBreadcrumb({
      message: 'Login with Apple',
      level: 'info',
      category: 'event',
    });
    const provider = new OAuthProvider('apple.com');
    try {
      const result = await signInWithPopup(auth, provider);

      // The signed-in user info.
      const user = result.user;

      const firebaseToken = await user.getIdToken();
      mutateLogin.mutate({ firebaseToken });
    } catch (error) {
      Sentry.captureException(error);
      console.log('Error: ', error);
    }
  };

  const renderTermAndConditions = () => {
    return (
      <>
        <div className="relative flex flex-row items-center justify-center">
          <button className="hidden cursor-pointer sm:block">
            <Link to="/">
              <BackArrow />
            </Link>
          </button>
          <span className="flex-1 text-center text-xl leading-[120%] font-semibold text-[#171315] sm:text-[24px]">
            We do not store your selfie
          </span>
        </div>

        <p className="my-4 h-[300px] overflow-y-auto text-base/[140%] font-normal text-[#171315] sm:h-auto sm:overflow-y-scroll sm:text-lg/[140%]">
          This consultation provides general skincare advice and product information only and is not
          a substitute for professional guidance. Your photo will not be stored and will be deleted
          immediately after analysis. By proceeding, you confirm that you are at least 18 years old
          and consent to Acie processing your personal data (including name, mobile number, email,
          and address) in accordance with the privacy policy outlined below. This data may be used
          for sending tailored brand promotions via various channels, including WhatsApp, as well as
          for research and analytics to enhance our services. To withdraw your consent, address
          concerns, or exercise your rights regarding your personal data, please refer to our{' '}
          <a className="underline" target="_blank" href="https://acie.skin/pages/privacy-policy">
            Privacy Policy
          </a>{' '}
          and{' '}
          <a className="underline" target="_blank" href="https://acie.skin/pages/terms-of-service">
            Terms & Conditions
          </a>
          .
        </p>

        <RadioButton
          checked={isPrivacyAccepted}
          onChange={function (): void {
            setIsPrivacyAccepted(!isPrivacyAccepted);
          }}
        >
          <span className="text-base/[140%] font-semibold text-[#171315] select-none sm:text-lg/[140%]">
            I understand and accept <span className="text-[#0063F3]">Privacy Policy</span>
          </span>
        </RadioButton>
        <RadioButton
          className="my-4"
          checked={isTermsAccepted}
          onChange={function (): void {
            setIsTermsAccepted(!isTermsAccepted);
          }}
        >
          <span className="text-base/[140%] font-semibold text-[#171315] select-none sm:text-lg/[140%]">
            I understand and accept <span className="text-[#0063F3]">Terms & Conditions</span>
          </span>
        </RadioButton>
        <Button
          text="I consent"
          onClick={() => {
            console.log('Terms and conditions accepted');
            // const accessToken = localStorage.getItem(StorageKey.ACCESS_TOKEN);
            // console.log('accessToken', accessToken);
            router.navigate({ to: '/skincheck' });
          }}
          iconPosition="left"
          buttonType={ButtonType.PRIMARY}
          disabled={!isTermsAccepted || !isPrivacyAccepted}
          size="medium"
        />
      </>
    );
  };

  const renderLoginForm = () => {
    return (
      <>
        <div className="relative flex flex-row items-center justify-center">
          <span className="flex-1 text-center text-xl leading-[120%] font-semibold text-[#171315] sm:text-[28px]">
            Get Started
          </span>
          {/* <Link to="/login" className="absolute right-0">
            Log in
          </Link> */}
        </div>

        <div className="absolute top-0 right-0">
          <p className="text-[12px] text-black">{userId}</p>
        </div>

        <span className="my-4 text-center text-base leading-[140%] font-normal text-[#353945]">
          Transform Your Skin Health with Acie™ Deeply Personalized Skincare™ Technology
        </span>

        <div className="flex flex-col py-[5rem]">
          <button
            onClick={() => {
              handleGoogleSignIn();
            }}
            className="relative flex cursor-pointer flex-row items-center justify-center rounded-2xl border-[1px] border-[#171315] px-5 py-4"
          >
            <GoogleIcon />
            <span className="color-[#171315 flex-1 text-center text-lg leading-[140%] font-semibold">
              Continue with Google
            </span>
          </button>
          <button
            onClick={() => {
              handleAppleSignIn();
            }}
            className="relative my-3 flex cursor-pointer flex-row items-center justify-center rounded-2xl border-[1px] border-[#171315] px-5 py-4"
          >
            <AppleIcon />
            <span className="color-[#171315 flex-1 text-center text-lg leading-[140%] font-semibold">
              Continue with Apple
            </span>
          </button>
          {/* <button
            onClick={() => {
              handleFacebookSignIn();
            }}
            className="relative flex cursor-pointer flex-row items-center justify-center rounded-2xl border-[1px] border-[#171315] px-5 py-4"
          >
            <FacebookIcon />
            <span className="color-[#171315 flex-1 text-center text-lg leading-[140%] font-semibold">
              Continue with Facebook
            </span>
          </button> */}
        </div>

        {/* <span className="my-4 text-center text-base leading-[140%] font-normal text-[#777E90]">
          Or you can choose to
        </span>

        <Button
          text="Sign up with Email"
          onClick={() => {}}
          iconPosition="left"
          buttonType={ButtonType.PRIMARY}
          size="medium"
        /> */}
      </>
    );
  };

  return (
    <div className="flex h-screen w-screen items-center justify-center bg-[url(src/assets/images/login/login-background.jpg)] bg-cover bg-no-repeat">
      <div className="flex w-[calc(100vw-32px)] flex-col rounded-[1.5rem] bg-white p-8 shadow-[0px_12px_24px_0px_#0000001F] sm:w-[30rem]">
        {!isAuth ? renderLoginForm() : renderTermAndConditions()}
      </div>
    </div>
  );
}
